import { useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Copy, Check } from "lucide-react";

const HEX_RE = /#(?:[0-9a-fA-F]{3}){1,2}\b/g;

/** User + assistant bubbles. `children` lets callers render custom body (e.g. images). */
export function ChatBubble({ role, children, streaming }) {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-4 py-3 text-[15px] leading-relaxed ${
          isUser
            ? "bg-slate-900 text-white rounded-br-md"
            : "bg-gray-50 text-slate-900 rounded-bl-md border border-slate-100"
        }`}
      >
        {children}
        {streaming && <span className="bh-typing" />}
      </div>
    </div>
  );
}

export function UserText({ children }) {
  return <div className="whitespace-pre-wrap">{children}</div>;
}

export function AssistantMarkdown({ content }) {
  const transformed = useMemo(() => wrapHexCodes(content || ""), [content]);
  return (
    <div className="prose-chat">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ node, ...p }) => <h1 className="font-medium text-xl mt-4 mb-2 tracking-tight" {...p} />,
          h2: ({ node, ...p }) => <h2 className="font-medium text-lg mt-4 mb-2 tracking-tight" {...p} />,
          h3: ({ node, ...p }) => <h3 className="font-medium text-base mt-3 mb-1.5 tracking-tight" {...p} />,
          p: ({ node, ...p }) => <p className="my-2" {...p} />,
          strong: ({ node, ...p }) => <strong className="font-semibold" {...p} />,
          em: ({ node, ...p }) => <em className="italic" {...p} />,
          ul: ({ node, ...p }) => <ul className="list-disc ml-5 my-2 space-y-1" {...p} />,
          ol: ({ node, ...p }) => <ol className="list-decimal ml-5 my-2 space-y-1" {...p} />,
          li: ({ node, ...p }) => <li className="leading-relaxed" {...p} />,
          a: ({ node, ...p }) => (
            <a className="underline underline-offset-2 text-slate-900" target="_blank" rel="noreferrer" {...p} />
          ),
          code: InlineOrBlockCode,
          pre: ({ node, children, ...p }) => (
            <pre
              className="bg-gray-100 border border-gray-200 rounded-lg p-3 my-3 overflow-x-auto text-sm font-mono"
              {...p}
            >
              {children}
            </pre>
          ),
          blockquote: ({ node, ...p }) => (
            <blockquote className="border-l-2 border-slate-300 pl-3 my-3 text-slate-600" {...p} />
          ),
        }}
      >
        {transformed}
      </ReactMarkdown>
    </div>
  );
}

function wrapHexCodes(md) {
  const parts = md.split(/(`[^`]+`|```[\s\S]+?```)/g);
  return parts
    .map((p, i) => (i % 2 === 1 ? p : p.replace(HEX_RE, (match) => `\`${match}\``)))
    .join("");
}

function InlineOrBlockCode({ inline, className, children, ...p }) {
  const value = String(children ?? "").replace(/\n$/, "");
  if (!inline) {
    return (
      <code className={className} {...p}>
        {children}
      </code>
    );
  }
  const isHex = /^#(?:[0-9a-fA-F]{3}){1,2}$/.test(value);
  if (isHex) return <HexBadge value={value} />;
  return (
    <code className="bg-gray-100 border border-gray-200 rounded px-1.5 py-0.5 font-mono text-[0.85em]">
      {children}
    </code>
  );
}

export function HexBadge({ value }) {
  const [copied, setCopied] = useState(false);
  const copy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch { /* ignore */ }
  };
  return (
    <span className="inline-flex items-center gap-1 align-baseline bg-gray-100 border border-gray-200 rounded px-1.5 py-0.5 font-mono text-[0.85em]">
      <span
        className="inline-block w-3 h-3 rounded-sm border border-black/10"
        style={{ backgroundColor: value }}
      />
      <span>{value}</span>
      <button
        onClick={copy}
        className="ml-0.5 text-slate-500 hover:text-slate-900"
        title={copied ? "Copied" : "Copy"}
      >
        {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
      </button>
    </span>
  );
}
